import { OptoColors } from '@/config/settings';

import type { ChartTheme } from '../theme';

import type { MetricLineGradient } from './seriesStyleTypes';

/** A colour stop of a threshold gradient, at `offset` from the bottom (0) to the top (1). */
export interface GradientStop {
    offset: number;
    color: string;
}

type Rgba = [number, number, number, number];

function parseColor(color: string): Rgba | null {
    const hex = /^#([0-9a-f]{3,8})$/i.exec(color.trim())?.[1];
    if (hex && (hex.length === 3 || hex.length === 6 || hex.length === 8)) {
        const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
        const channel = (i: number) => Number.parseInt(full.slice(i, i + 2), 16);
        return [channel(0), channel(2), channel(4), full.length === 8 ? channel(6) / 255 : 1];
    }
    const fn = /^rgba?\(([^)]+)\)$/i.exec(color.trim())?.[1];
    if (fn) {
        const parts = fn
            .split(/[\s,/]+/)
            .filter(Boolean)
            .map(Number);
        if (parts.length >= 3 && parts.every(Number.isFinite)) {
            return [parts[0], parts[1], parts[2], parts[3] ?? 1];
        }
    }
    return null;
}

/**
 * The colour a canvas gradient with `stops` shows at `offset`: interpolated between the two
 * stops around it, and the end colours beyond them, as the canvas draws it. A colour it cannot
 * read gives the nearest stop below instead of a guess.
 */
export function colorAtOffset(stops: GradientStop[], offset: number): string {
    const sorted = [...stops].sort((a, b) => a.offset - b.offset);
    const first = sorted[0];
    if (!first) return 'transparent';
    if (offset <= first.offset) return first.color;
    for (let i = 1; i < sorted.length; i++) {
        const below = sorted[i - 1];
        const above = sorted[i];
        if (offset > above.offset) continue;
        const span = above.offset - below.offset;
        const t = span > 0 ? (offset - below.offset) / span : 1;
        const from = parseColor(below.color);
        const to = parseColor(above.color);
        if (!from || !to) return t < 1 ? below.color : above.color;
        const mix = from.map((value, c) => value + (to[c] - value) * t);
        const [r, g, b] = mix.slice(0, 3).map(Math.round);
        return `rgba(${r}, ${g}, ${b}, ${Number(mix[3].toFixed(3))})`;
    }
    return sorted[sorted.length - 1].color;
}

/**
 * The gradient's stops, bottom to top, for an axis running from `min` to `max`. Each threshold
 * blends through the "around" colour over `transition` on either side of it.
 */
export function lineGradientStops(
    gradient: MetricLineGradient,
    theme: ChartTheme,
    min: number,
    max: number
): GradientStop[] {
    const good = theme.color(gradient.colors?.good ?? OptoColors.OceanGreen);
    const around = theme.color(gradient.colors?.around ?? OptoColors.Yellow);
    const bad = theme.color(gradient.colors?.bad ?? OptoColors.Red);
    const range = max - min;
    if (range <= 0) return [{ offset: 0, color: good }];
    const at = (value: number) => Math.max(0, Math.min(1, (value - min) / range));
    const transition = gradient.transition / range;

    if (gradient.kind === 'above') {
        const position = at(gradient.threshold);
        return [
            { offset: 0, color: good },
            { offset: Math.max(0, position - transition), color: good },
            { offset: position, color: around },
            { offset: Math.min(1, position + transition), color: bad },
        ];
    }

    const lower = at(gradient.lower);
    const upper = at(gradient.upper);
    return [
        { offset: 0, color: bad },
        { offset: Math.max(0, lower - transition), color: bad },
        { offset: lower, color: around },
        { offset: Math.min(1, lower + transition), color: good },
        { offset: Math.max(0, upper - transition), color: good },
        { offset: upper, color: around },
        { offset: Math.min(1, upper + transition), color: bad },
        { offset: 1, color: bad },
    ];
}

interface Area {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface Scale {
    min: number;
    max: number;
    getPixelForValue(value: number): number;
}

/** The parts of a Chart.js scriptable context the line colours read; a real context is one. */
export interface LineGradientContext {
    parsed?: { y?: number | null };
    chart: {
        ctx: Pick<CanvasRenderingContext2D, 'createLinearGradient'>;
        chartArea?: Area | null;
        scales: Record<string, Scale | undefined>;
    };
}

/** See `lineGradientColors`. */
export interface LineGradientColors {
    line(context: LineGradientContext): CanvasGradient | string;
    point(context: LineGradientContext): string;
}

/**
 * The `borderColor` and point colour of a line coloured by `gradient`.
 *
 * The line is a vertical canvas gradient over the plot area, built against the axis bounds
 * the chart actually drew, so it lines up with the scale whatever range the axis ended up
 * with. It is cached until the area or the bounds change: panning and zooming re-resolve
 * colours on every frame. A point takes the gradient's colour at its own height, so the hover
 * dot and the tooltip swatch match the line there rather than showing the whole gradient.
 * `fallback` is drawn before the chart has laid the plot out.
 */
export function lineGradientColors(
    gradient: MetricLineGradient,
    theme: ChartTheme,
    scaleId: string,
    fallback: string
): LineGradientColors {
    let cached: { key: string; value: CanvasGradient } | undefined;

    function bounds(context: LineGradientContext) {
        const area = context.chart.chartArea;
        const scale = context.chart.scales[scaleId];
        if (!area || !scale) return null;
        return { area, scale, stops: lineGradientStops(gradient, theme, scale.min, scale.max) };
    }

    return {
        line(context: LineGradientContext): CanvasGradient | string {
            const resolved = bounds(context);
            if (!resolved) return fallback;
            const { area, scale, stops } = resolved;
            const key = [area.top, area.bottom, scale.min, scale.max].join(':');
            if (cached?.key !== key) {
                const value = context.chart.ctx.createLinearGradient(0, area.bottom, 0, area.top);
                for (const stop of stops) value.addColorStop(stop.offset, stop.color);
                cached = { key, value };
            }
            return cached.value;
        },
        point(context: LineGradientContext): string {
            const value = context.parsed?.y;
            const resolved = bounds(context);
            if (typeof value !== 'number' || !Number.isFinite(value) || !resolved) return fallback;
            const { area, scale, stops } = resolved;
            const height = area.bottom - area.top;
            if (height <= 0) return fallback;
            return colorAtOffset(stops, (area.bottom - scale.getPixelForValue(value)) / height);
        },
    };
}
